import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';

/**
 * Traduce el **código de cierre** del contrato `g/o` (AMARU-862/US-005) a lo que el operario
 * necesita leer: por qué terminó así la operación.
 *
 * Por qué es un módulo hermano de `gateCommandOutcome.ts` y no una extensión suya: aquel clasifica
 * la respuesta **síncrona al comando** (`g/r`: GO00/GO03/GO09/GO1x más los códigos de transporte
 * del backend) y responde «¿salió el comando y qué puedo afirmar de la compuerta?». Éste lee el
 * cierre del **ciclo de vida** del movimiento (`g/o`: GO01/GO02 y GO20-GO2A/GO99) y responde «¿por
 * qué terminó así?». Son dos canales, dos espacios de códigos disjuntos y dos consumidores
 * distintos —el snackbar del comando contra el panel de operación—, así que un solo módulo tendría
 * que decidir primero cuál de las dos preguntas le están haciendo. Lo que sí se comparte es la
 * forma: función pura, `i18n` inyectado, y el default conservador que no afirma lo que no sabe.
 *
 * El discriminante importa: un atasco (GO23), un timeout (GO25) y una falla del driver (GO29)
 * llegan los tres como `FINALIZED_WITH_ERROR`. Sin esto el operario ve «finalizó con error» y no
 * sabe cuál de los tres tiene enfrente ni qué hacer al respecto.
 */
export type GateCloseOutcome = 'completed' | 'aborted' | 'unknown';

export interface GateCloseDescription {
  /** El código tal como lo informó el equipo, para reportarlo. */
  code: string;
  outcome: GateCloseOutcome;
  title: string;
  detail: string;
}

/** Forma de un código de operación del firmware: dos caracteres tras `GO` (`GO2A` incluido). */
const CLOSE_CODE = /^GO[0-9A-Z]{2}$/;

type CloseCodeEntry = {
  outcome: GateCloseOutcome;
  title: (i18n: I18n) => string;
  detail: (i18n: I18n) => string;
};

const CLOSE_CODES: Record<string, CloseCodeEntry> = {
  GO01: {
    outcome: 'completed',
    title: (i18n) => t(i18n)`Operación completada`,
    detail: (i18n) => t(i18n)`La compuerta llegó a la posición solicitada.`,
  },
  GO02: {
    outcome: 'completed',
    title: (i18n) => t(i18n)`Fin de carrera alcanzado`,
    detail: (i18n) =>
      t(i18n)`La compuerta llegó a su límite operativo y la operación terminó ahí.`,
  },
  GO20: {
    outcome: 'aborted',
    // No se afirma quién ordenó el stop: puede venir de Amaru, de otra integración o del equipo.
    title: (i18n) => t(i18n)`Operación detenida`,
    detail: (i18n) => t(i18n)`Se recibió una orden de detener antes de llegar al destino.`,
  },
  GO21: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por parada de emergencia`,
    detail: (i18n) =>
      t(i18n)`La parada de emergencia del tablero interrumpió el movimiento. No se podrá operar hasta deshabilitarla en terreno.`,
  },
  GO22: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación interrumpida por paso a modo manual`,
    detail: (i18n) =>
      t(i18n)`El automatizador pasó a modo manual durante el movimiento y dejó de obedecer el comando remoto.`,
  },
  GO23: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por atasco`,
    detail: (i18n) =>
      t(i18n)`La compuerta dejó de avanzar mientras el motor seguía activo. Revisar obstrucciones en el mecanismo.`,
  },
  GO24: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por error de lectura de posición`,
    detail: (i18n) =>
      t(i18n)`El equipo dejó de leer la posición de la compuerta y no pudo seguir controlando el movimiento.`,
  },
  GO25: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por tiempo máximo excedido`,
    detail: (i18n) =>
      t(i18n)`El movimiento superó el tiempo máximo permitido sin llegar al destino.`,
  },
  GO26: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por movimiento en sentido inverso`,
    detail: (i18n) =>
      t(i18n)`La compuerta se desplazó en el sentido contrario al solicitado. Revisar el conexionado del actuador.`,
  },
  GO27: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por fin de carrera de seguridad`,
    detail: (i18n) =>
      t(i18n)`Se activó un fin de carrera de seguridad durante el movimiento y el equipo cortó la operación.`,
  },
  GO28: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación interrumpida por apagado del equipo`,
    detail: (i18n) =>
      t(i18n)`El equipo se apagó o se reinició durante el movimiento y cerró la operación al volver. La posición de la compuerta puede no ser la que muestra el registro.`,
  },
  GO29: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por falla del actuador`,
    detail: (i18n) =>
      t(i18n)`El controlador del actuador reportó un error. Requiere revisión en terreno.`,
  },
  GO2A: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Operación detenida por entradas de seguridad ilegibles`,
    detail: (i18n) =>
      t(i18n)`El equipo dejó de leer las entradas de seguridad y abortó el movimiento por precaución.`,
  },
  // Cierres del carril de solicitudes encoladas (SGMO-14). SGMO los escribe en la misma columna
  // `close_code` con prefijo `Q` para no chocar con el catálogo `GO**` del firmware. Ninguno de
  // los tres describe un movimiento del equipo: la solicitud se cerró antes (o en lugar) de eso.
  QCANCEL: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Solicitud en cola cancelada`,
    detail: (i18n) =>
      t(i18n)`La solicitud se canceló con una detención antes de enviarse al equipo. La compuerta no se movió.`,
  },
  QEXPIRED: {
    outcome: 'aborted',
    title: (i18n) => t(i18n)`Solicitud en cola vencida`,
    detail: (i18n) =>
      t(i18n)`El equipo no se conectó antes del vencimiento y la solicitud se descartó sin enviarse. La compuerta no se movió.`,
  },
  QDUPMID: {
    outcome: 'completed',
    title: (i18n) => t(i18n)`Solicitud en cola entregada`,
    detail: (i18n) =>
      t(i18n)`El equipo aceptó la solicitud; la operación quedó registrada en su propio movimiento.`,
  },
  GO99: {
    outcome: 'unknown',
    title: (i18n) => t(i18n)`Operación finalizada por una causa desconocida`,
    detail: (i18n) => t(i18n)`El equipo no informó por qué terminó la operación.`,
  },
};

/** Normaliza lo que llegue del payload: solo un código con la forma del contrato cuenta. */
const readCloseCode = (code: unknown): string | null => {
  if (typeof code !== 'string') return null;
  const normalized = code.trim().toUpperCase();
  return normalized.length > 0 ? normalized : null;
};

/**
 * Descripción operacional del cierre, o `null` cuando el mensaje no trae código —ahí el consumidor
 * se queda con el texto genérico de su tipo, que es todo lo que se sabe.
 *
 * Un código con forma válida que esta versión del frontend no conoce **no** se descarta: se
 * devuelve como `unknown` con el código a la vista, para que el operario tenga algo que reportar.
 */
export const describeGateCloseCode = (
  code: unknown,
  i18n: I18n,
): GateCloseDescription | null => {
  const normalized = readCloseCode(code);
  if (normalized == null) return null;

  const entry = CLOSE_CODES[normalized];
  if (entry) {
    return {
      code: normalized,
      outcome: entry.outcome,
      title: entry.title(i18n),
      detail: entry.detail(i18n),
    };
  }

  if (!CLOSE_CODE.test(normalized)) return null;

  return {
    code: normalized,
    outcome: 'unknown',
    title: t(i18n)`Operación finalizada con un código no reconocido`,
    detail: t(i18n)`El equipo informó el código ${normalized}. Reportarlo junto con el código de la operación.`,
  };
};
